/* eslint-disable @typescript-eslint/no-explicit-any */
import { ApiError } from "../../types/ApiError";
import { axios } from "../../lib/axios";
import { CBQExamDTO } from "./InitCBQExam";

// The admin list returns the exam row plus the linking/gating fields that
// `CBQExamDTO` (the init payload) doesn't carry, and a scenario count when the
// backend includes one.
export interface AdminCBQExamListItem extends CBQExamDTO {
  lectureId?: string | null;
  courseId?: string | null;
  quizId?: string | null;
  requiredQuizScore?: number | null;
  scenariosCount?: number | null;
  _count?: { scenarios?: number } | null;
}

export interface GetAdminCBQExamsParams {
  courseId?: string;
  lectureId?: string;
  quizId?: string;
  status?: string;
  mode?: string;
  q?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

type GetAdminCBQExamsResponse = {
  statusCode?: number;
  error?: null | ApiError;
  data?: AdminCBQExamListItem[];
  total?: number;
};

// The endpoint may answer with a bare array, `{ data: [...] }`, or a paginated
// envelope. Normalise all three so callers only ever see a list.
const unwrapList = (payload: any): AdminCBQExamListItem[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.exams)) return payload.exams;
  return [];
};

// GET /admin/cbq/exams — admin listing, filtered to one course when courseId is
// given. Undefined params are dropped so we never send `?courseId=undefined`.
export const GetAdminCBQExams = async (
  params: GetAdminCBQExamsParams = {}
): Promise<GetAdminCBQExamsResponse> => {
  const query = Object.fromEntries(
    Object.entries(params).filter(
      ([, v]) => v !== undefined && v !== null && v !== ""
    )
  );
  try {
    const response = await axios.get(`/admin/cbq/exams`, { params: query });
    const payload = response.data?.data ?? response.data;
    return {
      data: unwrapList(payload),
      total: payload?.total ?? payload?.meta?.total,
    };
  } catch (error: any) {
    console.error("API Error:", error);
    if (error.response) {
      // No exams yet reads as an empty list, not an error page.
      if (error.response.status === 404) return { data: [] };
      const apiError: ApiError = {
        statusCode: error.response.status,
        message: error.response.data?.message,
        error: error.response.data?.error,
      };
      return { error: apiError };
    } else {
      const fallbackError: ApiError = {
        statusCode: 500,
        message: "An unexpected error occurred",
      };
      return { error: fallbackError };
    }
  }
};
